import { motion } from "framer-motion";
import { Target, Award, Crown, Star, TrendingUp, Zap, Timer, Trophy, Medal, Flame, ArrowRight, Gift, CheckCircle2 } from "lucide-react";
import heroBg from "../assets/hero-bg.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyParticles from "../components/EnergyParticles";
import { Link } from "react-router-dom";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const ranks = [
  { name: "Energy Builder", icon: Zap, requirement: "$5,000 Team Volume", rewards: ["$100 Rank Bonus", "Bronze Badge", "Priority Support"], color: "from-amber-600 to-amber-400" },
  { name: "Wind Leader", icon: Star, requirement: "$25,000 Team Volume", rewards: ["$500 Rank Bonus", "Silver Badge", "VIP Support", "Monthly Strategy Call"], color: "from-slate-400 to-slate-200" },
  { name: "Solar Director", icon: Crown, requirement: "$100,000 Team Volume", rewards: ["$2,500 Rank Bonus", "Gold Badge", "1:1 Mentorship", "Annual Summit Invite"], color: "from-yellow-500 to-yellow-300" },
  { name: "Global Ambassador", icon: Trophy, requirement: "$500,000 Team Volume", rewards: ["$10,000 Rank Bonus", "Diamond Badge", "Advisory Board Seat", "Luxury Retreat", "Profit Share Pool"], color: "from-primary to-accent" },
];

const TargetRewards = () => (
  <div>
    <PageHero
      eyebrow="Targets & Rewards"
      title={<>Achieve. Lead. <span className="energy-gradient-text">Earn.</span></>}
      description="Ambitious targets drive extraordinary results. Hit your performance milestones and unlock escalating rewards, recognition, and exclusive leadership opportunities."
      backgroundImage={heroBg}
    />

    {/* Short-Term Targets */}
    <Section>
      <SectionHeading eyebrow="Quick Targets" title="7 / 10 / 15 Day Performance Targets" description="Fast-track rewards for early momentum. Achieve these targets within the specified timeframe to earn instant bonuses." />
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { days: 7, title: "Sprint Target", icon: Timer, reward: "$50 Bonus", requirement: "3 Direct Referrals + $1,500 Volume", desc: "Quick start incentive for new partners who hit the ground running." },
          { days: 10, title: "Momentum Target", icon: Flame, reward: "$150 Bonus", requirement: "5 Direct Referrals + $5,000 Volume", desc: "Sustained early growth reward for consistent performers." },
          { days: 15, title: "Acceleration Target", icon: TrendingUp, reward: "$400 Bonus", requirement: "8 Direct Referrals + $12,000 Volume", desc: "Premium early achiever bonus for exceptional network builders." },
        ].map((t, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.15 }} whileHover={{ y: -5 }} className="p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/0 via-primary/50 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl energy-gradient flex items-center justify-center group-hover:shadow-[0_0_20px_hsl(155_80%_50%/0.3)] transition-shadow">
                <t.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-bold text-primary glow-text">{t.days}</div>
                <div className="text-[10px] text-muted-foreground">Days</div>
              </div>
            </div>
            <h3 className="font-display text-lg font-bold mb-2">{t.title}</h3>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{t.desc}</p>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2"><Target className="w-3 h-3 text-primary" /><span className="text-muted-foreground">{t.requirement}</span></div>
              <div className="flex items-center gap-2"><Gift className="w-3 h-3 text-primary" /><span className="font-semibold text-primary">{t.reward}</span></div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Monthly Targets */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Monthly Goals" title="Monthly Individual Targets" description="Consistent monthly performance unlocks tiered bonuses. The more you achieve, the higher the rewards." />
        <div className="max-w-3xl mx-auto space-y-3">
          {[
            { tier: "Bronze", volume: "$5,000", bonus: "$100", pct: 25 },
            { tier: "Silver", volume: "$15,000", bonus: "$400", pct: 50 },
            { tier: "Gold", volume: "$40,000", bonus: "$1,200", pct: 75 },
            { tier: "Platinum", volume: "$100,000+", bonus: "$4,000", pct: 100 },
          ].map((m, i) => (
            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="flex items-center gap-4 p-4 rounded-xl glass-card glass-card-hover transition-all duration-500">
              <div className="w-12 h-12 rounded-xl energy-gradient flex items-center justify-center">
                <Medal className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-display text-sm font-semibold">{m.tier} Tier</span>
                  <span className="text-xs font-bold text-primary">{m.bonus}</span>
                </div>
                <div className="text-[10px] text-muted-foreground mb-2">Monthly Volume: {m.volume}</div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <motion.div className="h-full energy-gradient rounded-full" initial={{ width: 0 }} whileInView={{ width: `${m.pct}%` }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.8 }} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>

    {/* Team Volume Rewards */}
    <Section>
      <SectionHeading eyebrow="Team Performance" title="Team Volume Rewards" description="When your team hits collective milestones, everyone benefits. These rewards recognize outstanding leadership." />
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {[
          { milestone: "$50,000 Team Volume", reward: "Team Leadership Bonus — $500", desc: "Recognized as a team leader with a cash bonus and platform badge." },
          { milestone: "$150,000 Team Volume", reward: "Performance Bonus — $2,000", desc: "High-performance teams earn enhanced bonuses and event invitations." },
          { milestone: "$500,000 Team Volume", reward: "Elite Pool Share — Variable", desc: "Access to the monthly elite performers' revenue pool." },
          { milestone: "$1,000,000+ Team Volume", reward: "Global Leader Fund — Variable", desc: "Lifetime membership in the Global Leader Fund with profit participation." },
        ].map((tv, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-5 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-primary">{tv.milestone}</span>
            </div>
            <h3 className="font-display text-sm font-bold mb-2">{tv.reward}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{tv.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Leader Ranks */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Leadership Ranks" title="Rise Through the Ranks" description="Four prestigious ranks recognize your leadership and contribution to the AeroVolt ecosystem." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ranks.map((r, i) => (
            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.12 }} whileHover={{ y: -8 }} className="p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-16 h-16 rounded-full energy-gradient flex items-center justify-center mx-auto mb-4 group-hover:shadow-[0_0_25px_hsl(155_80%_50%/0.4)] transition-shadow animate-glow-pulse">
                <r.icon className="w-8 h-8 text-primary-foreground" />
              </div>
              <h3 className="font-display text-base font-bold mb-2">{r.name}</h3>
              <div className="text-xs text-primary font-medium mb-4">{r.requirement}</div>
              <div className="space-y-2">
                {r.rewards.map((rw, j) => (
                  <div key={j} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0" />
                    <span>{rw}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>

    {/* CTA */}
    <Section>
      <div className="text-center max-w-2xl mx-auto">
        <Trophy className="w-12 h-12 text-primary mx-auto mb-6 animate-pulse-glow" />
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Start Achieving Today</h2>
        <p className="text-muted-foreground mb-8">Choose your plan, set your targets, and unlock rewards as you grow within the AeroVolt ecosystem.</p>
        <Link to="/packages" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all glow-box hover:glow-box-hover text-sm">
          View Energy Plans <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  </div>
);

export default TargetRewards;
